import { ApiResponse } from '@/domain';
import { BillingType } from '@/domain/invoices/invoice';
import { PaymentStatus } from '@/domain/payments/payments';
import { useToast } from '@/hooks/use-toast';
import { paymentsApi, UpdateAgreementPaymentRequest } from '@/rest/modules/payments-api';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { agreementKeys } from './useAgreements';
import { invoiceKeys } from './useInvoices';
import { subscriptionKeys } from './useSubscriptions';

export const paymentKeys = {
  all: ['payments'] as const,
  lists: () => [...paymentKeys.all, 'list'] as const,
  list: (filters: any) => [...paymentKeys.lists(), { filters }] as const,
  agreement: (contractId: string, paymentId: string) => [...paymentKeys.lists(), { contractId, paymentId }] as const,
  agreementPayments: (contractId: string) => ['agreement-payments', contractId] as const,
  contractPayment: (contractId: string, paymentId: string) => [...paymentKeys.lists(), 'contract-payment', { contractId, paymentId }] as const,
  installmentPayment: (contractId: string, paymentId: string) => [...paymentKeys.lists(), 'installment-payment', { contractId, paymentId }] as const,
  paymentData: (paymentId: string) => [...paymentKeys.all, 'payment-data', paymentId] as const,
};

export const useAgreementPayment = (contractId: string, paymentId: string) => {
  return useQuery({
    queryKey: paymentKeys.agreement(contractId, paymentId),
    queryFn: () => paymentsApi.getAgreementPayment(contractId, paymentId),
    enabled: !!contractId && !!paymentId,
  });
};

export const useSubscriptionPayment = (billingPeriodId: string, paymentId: string, options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: [...paymentKeys.all, 'subscription', billingPeriodId, paymentId],
    queryFn: () => paymentsApi.getSubscriptionPayment(billingPeriodId, paymentId),
    enabled: (options?.enabled ?? true) && !!billingPeriodId && !!paymentId,
  });
};

export const useSubscriptionMulitPayment = (subscriptionId: string, paymentId: string, options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: [...paymentKeys.all, 'subscription', subscriptionId, paymentId],
    queryFn: () => paymentsApi.getSubscriptionMultiPayment(subscriptionId, paymentId),
    enabled: (options?.enabled ?? true) && !!subscriptionId && !!paymentId,
  });
};

export const useInstallmentPayment = (contractId: string, paymentId: string, options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: paymentKeys.installmentPayment(contractId, paymentId),
    queryFn: () => paymentsApi.getInstallmentPayment(contractId, paymentId),
    enabled: (options?.enabled ?? true) && !!contractId && !!paymentId,
  });
};

export const useAgreementCustomer = (contractId: string) => {
  return useQuery({
    queryKey: ['agreement-customer', contractId],
    queryFn: () => paymentsApi.getAgreementCustomer(contractId),
    enabled: !!contractId,
    staleTime: 100
  });
};

export const useAgreementPayments = (contractId: string, options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: paymentKeys.agreementPayments(contractId),
    queryFn: () => paymentsApi.getAgreementPayments(contractId),
    enabled: (options?.enabled ?? true) && !!contractId,
    gcTime: 0,
  })
}

export const useAgreementByPayment = (paymentId: string) => {
  return useQuery({
    queryKey: [...paymentKeys.all, 'payments', paymentId],
    queryFn: () => paymentsApi.getAgreementByPayment(paymentId),
    enabled: !!paymentId,
  });
};

export const useMultiPaymentData = (paymentId: string) => {
  return useQuery({
    queryKey: paymentKeys.paymentData(paymentId),
    queryFn: () => paymentsApi.getMultiPaymentData(paymentId),
    enabled: !!paymentId,
    staleTime: 0,
  });
};

export const useAgreementsByPayments = (paymentIds: string[]) => {
  return useQuery({
    queryKey: [...paymentKeys.all, 'payments-bulk-agreements', paymentIds],
    queryFn: () => paymentsApi.getAgreementsByPayments(paymentIds),
    enabled: paymentIds.length > 0,
  });
};

export const useUpdatePayment = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ contractId, paymentId, data }: { contractId: string; paymentId: string; data: Partial<UpdateAgreementPaymentRequest> }) =>
      paymentsApi.updateAgreementPayment(contractId, paymentId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: paymentKeys.agreement(variables.contractId, variables.paymentId) });
      queryClient.invalidateQueries({ queryKey: agreementKeys.lists() });
      queryClient.invalidateQueries({ queryKey: agreementKeys.detail(variables.contractId) });
      toast({
        title: 'Éxito',
        description: 'Pago actualizado exitosamente',
        variant: 'default',
      });
    },
    onError: (response: ApiResponse<any>) => {
      toast({
        title: 'Error',
        description: response.message || response.error || 'Error al actualizar el pago',
        variant: 'destructive',
      });
    },
  });
};

export const useResetInstallmentPayment = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ contractId, paymentId }: { contractId: string; paymentId: string }) =>
      paymentsApi.updatePayment(paymentId, { status: PaymentStatus.PENDING }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: paymentKeys.installmentPayment(variables.contractId, variables.paymentId) });
      queryClient.invalidateQueries({ queryKey: agreementKeys.lists() });
      queryClient.invalidateQueries({ queryKey: agreementKeys.detail(variables.contractId) });
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.installments(variables.contractId) });
      queryClient.invalidateQueries({ queryKey: paymentKeys.agreementPayments(variables.contractId), exact: false });
      toast({
        title: 'Éxito',
        description: 'Pago restaurado a estado pendiente',
        variant: 'default',
      });
    },
    onError: (response: ApiResponse<any>) => {
      toast({
        title: 'Error',
        description: response.message || response.error || 'Error al restaurar el pago',
        variant: 'destructive',
      });
    },
  });
};

export const useDeletePayment = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ contractId, paymentId }: { contractId: string; paymentId: string }) =>
      paymentsApi.deleteAgreementPayment(contractId, paymentId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: agreementKeys.lists() });
      queryClient.invalidateQueries({ queryKey: agreementKeys.detail(variables.contractId) });
      toast({
        title: 'Éxito',
        description: 'Operación cancelada exitosamente',
        variant: 'default',
      });
    },
    onError: (response: ApiResponse<any>) => {
      toast({
        title: 'Error',
        description: response.message || response.error || 'Error al cancelar la operación',
        variant: 'destructive',
      });
    },
  });
};

export const useDeleteDirectPayment = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ paymentId, contractId }: { paymentId: string; contractId: string }) =>
      paymentsApi.deletePayment(paymentId),
    onSuccess: (_, { contractId }) => {
      console.log(contractId)
      queryClient.invalidateQueries({ queryKey: paymentKeys.lists() });
      queryClient.invalidateQueries({ queryKey: agreementKeys.lists() });
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.lists() });
      queryClient.invalidateQueries({ queryKey: paymentKeys.agreementPayments(contractId), exact: false });
      toast({
        title: 'Éxito',
        description: 'Pago eliminado exitosamente',
        variant: 'default',
      });
    },
    onError: (response: ApiResponse<any>) => {
      toast({
        title: 'Error',
        description: response.message || response.error || 'Error al eliminar el pago',
        variant: 'destructive',
      });
    },
  });
};

export const useUpdatePaymentCollector = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ paymentId, collectorId }: { paymentId: string; collectorId: string | null }) =>
      paymentsApi.updatePayment(paymentId, { collectorId }),
    onSuccess: (_, { paymentId }) => {
      queryClient.invalidateQueries({ queryKey: paymentKeys.paymentData(paymentId) });
      queryClient.invalidateQueries({ queryKey: paymentKeys.lists() });
      toast({
        title: 'Éxito',
        description: 'Cobrador actualizado exitosamente',
        variant: 'default',
      });
    },
    onError: (response: ApiResponse<any>) => {
      toast({
        title: 'Error',
        description: response.message || response.error || 'Error al actualizar el cobrador',
        variant: 'destructive',
      });
    },
  });
};

export const useCreateMultiPayment = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ contractId, paymentIds }: { contractId: string; paymentIds: string[] }) =>
      paymentsApi.createMultiPayment(paymentIds),
    onSuccess: (response, { contractId }) => {
      queryClient.invalidateQueries({ queryKey: paymentKeys.lists() });
      queryClient.invalidateQueries({ queryKey: agreementKeys.lists() });
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.subscriptions(contractId) });
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.installments(contractId) });
      queryClient.invalidateQueries({ queryKey: paymentKeys.agreementPayments(contractId), exact: false });
      toast({
        title: 'Éxito',
        description: 'Pago agrupado creado exitosamente',
        variant: 'default',
      });
      return response
    },
    onError: (response: ApiResponse<any>) => {
      toast({
        title: 'Error',
        description: response.message || response.error || 'Error al agrupar los pagos',
        variant: 'destructive',
      });
    },
  });
};

export const useInvoicePayments = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ paymentIds, billingType }: { paymentIds: string[]; billingType: BillingType }) =>
      paymentsApi.invoicePayments(paymentIds, billingType),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: paymentKeys.lists() });
      queryClient.invalidateQueries({ queryKey: invoiceKeys.all });
      toast({
        title: 'Éxito',
        description: 'Facturación iniciada exitosamente',
        variant: 'default',
      });
      return response;
    },
    onError: (response: ApiResponse<any>) => {
      toast({
        title: 'Error',
        description: response.message || response.error || 'Error al facturar los pagos',
        variant: 'destructive',
      });
    },
  });
};

export const useRefundPayments = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (paymentIds: string[]) =>
      paymentsApi.refundPayments(paymentIds),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: paymentKeys.lists() });
      queryClient.invalidateQueries({ queryKey: invoiceKeys.all });
      toast({
        title: 'Éxito',
        description: 'Reembolso iniciado exitosamente',
        variant: 'default',
      });
      return response;
    },
    onError: (response: ApiResponse<any>) => {
      toast({
        title: 'Error',
        description: response.message || response.error || 'Error al reembolsar los pagos',
        variant: 'destructive',
      });
    },
  });
};

